import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import mineCraftApi from 'src/api/mineCraftApi';
import minecraftUrls from "src/configs/mine_craft/minecraftUrls";
import { AvatarInterface } from 'src/store/mine_craft/avatarsSlice';
import { usersEntity } from "./usersActions";
import { UserInterface } from "../../store/mine_craft/usersSlice";

const processStrategy = (value: any) => {
  return {
    ...value,
    image_url: minecraftUrls.remoteUrl(value.image_url),
  };
}

export const avatarsEntity = new schema.Entity(
  'avatars',
  {
    user: usersEntity
  },
  {
    processStrategy
  }
);

export const avatarEntity = new schema.Entity(
  'avatar',
  {
    user: usersEntity
  },
  {
    processStrategy
  }
);

const fetchAvatarsCollection = async (url: string, params: object = {}) => {
  const response    = await mineCraftApi.get(url, { params })
  const normalized  = normalize<
    any,
    {
      users: { [key: string]: UserInterface };
      avatars: { [key: string]: AvatarInterface };
    }
  >(response.data.avatars, [avatarsEntity]);

  return {
    users: normalized.entities.users || {},
    avatars: normalized.entities?.avatars || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      user: { [key: string]: UserInterface };
      avatar: AvatarInterface;
    }
  >(response.data.avatar, avatarEntity);
  return normalized.entities;
}

const normalizeUploadInstance = (response: any) => {
  const normalized = normalize<
    any,
    {
      avatar: AvatarInterface
    }
  >(response.avatar, avatarEntity);
  return normalized.entities;
}

export const fetchAvatars = createAsyncThunk(
  'avatars/fetchAvatars',
  async (params: object = {}) => {
    return fetchAvatarsCollection('/onesky/avatars', params);
  }
);

export const fetchAvatar = createAsyncThunk(
  'avatars/fetchAvatar',
  async (id: number | string) => {
    const response = await mineCraftApi.get(`/onesky/avatars/${id}`);
    return normalizeInstance(response);
  }
);

export const uploadAvatar = createAsyncThunk(
  'avatars/uploadAvatar',
  async (response: object, { rejectWithValue }) => {
    try {
      return normalizeUploadInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const createAvatar = createAsyncThunk(
  'avatars/createAvatar',
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/avatars`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateAvatar = createAsyncThunk(
  'avatars/updateAvatar',
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/avatars/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyAvatar = createAsyncThunk(
  'avatars/destroyAvatar',
  async (id: number) => {
    const response = await mineCraftApi.delete(`/onesky/avatars/${id}`);
    return response.data;
  }
);

export const reorderAvatars = createAsyncThunk(
  'avatars/reorderAvatars',
  async ({ ids }: { ids: any }, { rejectWithValue }) => {
    try {
      await mineCraftApi.patch('/onesky/avatars/reorder', { ids })

      return { ids };
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);
